import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import ExplorerLayout from '../../components/shell/ExplorerTree';
import { useCourseExplorer } from '../../components/courses/CourseExplorer';
import { NODE_KIND_LABEL } from '../../hooks/useCatalogTree';
import { useTabLabel } from '../../components/shell/TabsContext';
import RecordPage from '../../components/shell/RecordPage';
import { StatusBadge } from '../../components/datagrid/DataGrid';
import { Button } from '../../components/ui/button';
import { PaneFooter } from '../../components/shell/SidePane';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import {
  Plus,
  Trash,
  Pencil,
  Eye,
  FolderSimple,
  File,
  FilePdf,
  FileDoc,
  Video,
  MusicNote,
  Link as LinkIcon,
  Exam,
  CloudArrowUp,
  Copy,
  ArrowUp,
  ArrowDown,
  ArrowBendUpLeft,
  ArrowBendDownRight,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Document, Page, pdfjs } from 'react-pdf';
import { openLearnerPreview } from '../../components/PreviewMode';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Helper function to convert video URLs to embed format
const getEmbedUrl = (url) => {
  if (!url) return null;
  
  // YouTube regular URL: https://www.youtube.com/watch?v=VIDEO_ID
  const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
  if (youtubeMatch) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
  }
  
  // Vimeo: https://vimeo.com/VIDEO_ID
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }
  
  // Cloudflare Stream embed URL
  if (url.includes('cloudflarestream.com') || url.includes('videodelivery.net')) {
    if (url.includes('/iframe/') || url.includes('iframe.cloudflarestream.com')) {
      return url;
    }
    const streamIdMatch = url.match(/(?:cloudflarestream\.com|videodelivery\.net)\/([a-zA-Z0-9]+)/);
    if (streamIdMatch) {
      return `https://iframe.cloudflarestream.com/${streamIdMatch[1]}`;
    }
  }
  
  return url;
};

const getNodeIcon = (nodeType, contentType) => {
  if (nodeType === 'section' || nodeType === 'subsection') return FolderSimple;
  if (nodeType === 'exam') return Exam;
  if (contentType === 'pdf') return FilePdf;
  if (contentType === 'ppt' || contentType === 'docx') return FileDoc;
  if (contentType === 'video') return Video;
  if (contentType === 'audio') return MusicNote;
  if (contentType === 'external_link') return LinkIcon;
  if (nodeType === 'file') return CloudArrowUp;
  if (contentType === 'text_audio') return MusicNote;
  return File;
};

// Flatten tree structure for display
const flattenTree = (nodes, depth = 0, result = []) => {
  nodes.forEach(node => {
    result.push({ ...node, depth });
    if (node.children && node.children.length > 0) {
      flattenTree(node.children, depth + 1, result);
    }
  });
  return result;
};

// PDF Viewer Component
function PdfViewer({ filePath, token }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfData, setPdfData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scale, setScale] = useState(1.0);
  const [fitMode, setFitMode] = useState('width');
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(600);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth - 40);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  useEffect(() => {
    const fetchPdf = async () => {
      try {
        setLoading(true);
        setError(null);
        const { getBackendUrl } = await import('../../config/api');
        const backendUrl = getBackendUrl();
        const actualPath = filePath.includes('|') ? filePath.split('|')[0] : filePath;
        
        let url;
        if (actualPath.startsWith('/api/files/')) {
          // New format: /api/files/{id} — use stream endpoint with token
          url = `${backendUrl}${actualPath}/stream?token=${encodeURIComponent(token)}`;
        } else {
          // Legacy format: Emergent storage path — use serve endpoint with token
          url = `${backendUrl}/api/files/serve?path=${encodeURIComponent(actualPath)}&token=${encodeURIComponent(token)}`;
        }
        
        const response = await fetch(url);
        
        if (!response.ok) throw new Error('Failed to fetch PDF');
        
        const blob = await response.blob();
        const dataUrl = URL.createObjectURL(blob);
        setPdfData(dataUrl);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (filePath && token) {
      fetchPdf();
    }
    
    return () => {
      if (pdfData) URL.revokeObjectURL(pdfData);
    };
  }, [filePath, token]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-muted/20 rounded-lg">
        <div className="animate-pulse text-muted-foreground">Loading PDF...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 bg-red-50 dark:bg-red-900/20 rounded-lg">
        <p className="text-red-600 dark:text-red-400">Failed to load PDF: {error}</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="pdf-viewer space-y-4">
      <div className="flex items-center justify-between bg-muted/50 p-2 rounded-lg flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPageNumber(Math.max(1, pageNumber - 1))} disabled={pageNumber <= 1}>
            Previous
          </Button>
          <span className="text-sm px-2">Page {pageNumber} of {numPages || '?'}</span>
          <Button variant="outline" size="sm" onClick={() => setPageNumber(Math.min(numPages || 1, pageNumber + 1))} disabled={pageNumber >= (numPages || 1)}>
            Next
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={fitMode === 'width' ? 'secondary' : 'outline'} size="sm" onClick={() => setFitMode('width')}>Fit Width</Button>
          <Button variant={fitMode === 'page' ? 'secondary' : 'outline'} size="sm" onClick={() => setFitMode('page')}>Fit Page</Button>
          <Button variant={fitMode === 'custom' ? 'secondary' : 'outline'} size="sm" onClick={() => setFitMode('custom')}>Custom</Button>
          {fitMode === 'custom' && (
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" onClick={() => setScale(s => Math.max(0.5, s - 0.1))}>-</Button>
              <span className="text-sm w-12 text-center">{Math.round(scale * 100)}%</span>
              <Button variant="outline" size="sm" onClick={() => setScale(s => Math.min(2.0, s + 0.1))}>+</Button>
            </div>
          )}
        </div>
      </div>
      <div className="border rounded-lg overflow-auto bg-gray-100 dark:bg-gray-900" style={{ maxHeight: '70vh' }}>
        <Document file={pdfData} onLoadSuccess={onDocumentLoadSuccess} loading={<div className="p-8 text-center">Loading document...</div>}>
          <Page
            pageNumber={pageNumber}
            width={fitMode === 'width' ? containerWidth : undefined}
            height={fitMode === 'page' ? 600 : undefined}
            scale={fitMode === 'custom' ? scale : undefined}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        </Document>
      </div>
    </div>
  );
}

// Shared by the copy / import panes
const flattenNodesForSelect = (nodes, depth = 0) => {
  let result = [];
  nodes.forEach(node => {
    result.push({ ...node, depth });
    if (node.children && node.children.length > 0) {
      result = result.concat(flattenNodesForSelect(node.children, depth + 1));
    }
  });
  return result;
};

const ROOT_PARENT = '__root__'; // Radix Select forbids an empty-string item value


// Copy node → side pane ("Copy node to another course")
function useCopyNodePane({ isOpen, onClose, sourceNode, currentCourseId, api, onSuccess }) {
  const [copyableCourses, setCopyableCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedParent, setSelectedParent] = useState('');
  const [includeChildren, setIncludeChildren] = useState(true);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchCopyableCourses();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCopyableCourses = async () => {
    try {
      setFetching(true);
      const res = await api.get(`/courses/${currentCourseId}/copyable-nodes`);
      setCopyableCourses(res.data);
    } catch (error) {
      toast.error('Failed to load courses');
    } finally {
      setFetching(false);
    }
  };

  const selectedCourseData = copyableCourses.find(c => c.course_id === selectedCourse);
  const parentOptions = selectedCourseData ? flattenNodesForSelect(selectedCourseData.nodes).filter(n => n.node_type === 'section' || n.node_type === 'subsection') : [];

  const handleCopy = async () => {
    if (!selectedCourse) {
      toast.error('Please select a target course');
      return;
    }

    try {
      setLoading(true);
      await api.post(`/courses/${selectedCourse}/nodes/copy`, {
        source_node_id: sourceNode.id,
        source_course_id: currentCourseId,
        target_parent_id: selectedParent || null,
        target_order_index: 999,
        include_children: includeChildren
      });
      toast.success('Node copied successfully');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to copy node');
    } finally {
      setLoading(false);
    }
  };

  const body = fetching ? (
    <div className="py-8 text-center text-muted-foreground">Loading courses...</div>
  ) : copyableCourses.length === 0 ? (
    <div className="py-8 text-center text-muted-foreground">
      No other courses available to copy to.
    </div>
  ) : (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Copy "{sourceNode?.title}" to another course in your organization.</p>
      <div className="field">
        <Label>Target Course</Label>
        <Select value={selectedCourse} onValueChange={(val) => { setSelectedCourse(val); setSelectedParent(''); }}>
          <SelectTrigger data-testid="copy-target-course">
            <SelectValue placeholder="Select a course" />
          </SelectTrigger>
          <SelectContent>
            {copyableCourses.map(course => (
              <SelectItem key={course.course_id} value={course.course_id}>
                {course.course_title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedCourse && parentOptions.length > 0 && (
        <div className="field">
          <Label>Parent chapter / topic (optional)</Label>
          <Select value={selectedParent || ROOT_PARENT} onValueChange={(v) => setSelectedParent(v === ROOT_PARENT ? '' : v)}>
            <SelectTrigger data-testid="copy-target-parent">
              <SelectValue placeholder="Root level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ROOT_PARENT}>Root level</SelectItem>
              {parentOptions.map(node => (
                <SelectItem key={node.id} value={node.id}>
                  {'  '.repeat(node.depth)}{node.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex items-center space-x-2">
        <Checkbox id="include-children" checked={includeChildren} onCheckedChange={setIncludeChildren} />
        <Label htmlFor="include-children" className="cursor-pointer">Include child nodes</Label>
      </div>
    </div>
  );

  const footer = (
    <PaneFooter onCancel={onClose} onSubmit={handleCopy} submitLabel={loading ? 'Copying...' : 'Copy Node'} submitting={loading} submitDisabled={!selectedCourse} submitIcon={Copy} />
  );

  return { body, footer };
}

// Import node → side pane ("Import from course")
function useImportNodePane({ isOpen, onClose, currentCourseId, targetParentId, api, onSuccess }) {
  const [copyableCourses, setCopyableCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedNode, setSelectedNode] = useState('');
  const [includeChildren, setIncludeChildren] = useState(true);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchCopyableCourses();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCopyableCourses = async () => {
    try {
      setFetching(true);
      const res = await api.get(`/courses/${currentCourseId}/copyable-nodes`);
      setCopyableCourses(res.data);
    } catch (error) {
      toast.error('Failed to load courses');
    } finally {
      setFetching(false);
    }
  };

  const selectedCourseData = copyableCourses.find(c => c.course_id === selectedCourse);
  const nodeOptions = selectedCourseData ? flattenNodesForSelect(selectedCourseData.nodes) : [];

  const handleImport = async () => {
    if (!selectedCourse || !selectedNode) {
      toast.error('Please select a course and node');
      return;
    }

    try {
      setLoading(true);
      await api.post(`/courses/${currentCourseId}/nodes/copy`, {
        source_node_id: selectedNode,
        source_course_id: selectedCourse,
        target_parent_id: targetParentId || null,
        target_order_index: 999,
        include_children: includeChildren
      });
      toast.success('Node imported successfully');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import node');
    } finally {
      setLoading(false);
    }
  };

  const body = fetching ? (
    <div className="py-8 text-center text-muted-foreground">Loading courses...</div>
  ) : copyableCourses.length === 0 ? (
    <div className="py-8 text-center text-muted-foreground">
      No other courses available to import from.
    </div>
  ) : (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Copy content from another course into this one.</p>
      <div className="field">
        <Label>Source Course</Label>
        <Select value={selectedCourse} onValueChange={(val) => { setSelectedCourse(val); setSelectedNode(''); }}>
          <SelectTrigger data-testid="import-source-course">
            <SelectValue placeholder="Select a course" />
          </SelectTrigger>
          <SelectContent>
            {copyableCourses.map(course => (
              <SelectItem key={course.course_id} value={course.course_id}>
                {course.course_title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedCourse && nodeOptions.length > 0 && (
        <div className="field">
          <Label>Node to Import</Label>
          <Select value={selectedNode} onValueChange={setSelectedNode}>
            <SelectTrigger data-testid="import-source-node">
              <SelectValue placeholder="Select a node" />
            </SelectTrigger>
            <SelectContent>
              {nodeOptions.map(node => (
                <SelectItem key={node.id} value={node.id}>
                  {'  '.repeat(node.depth)}{node.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex items-center space-x-2">
        <Checkbox id="include-children-import" checked={includeChildren} onCheckedChange={setIncludeChildren} />
        <Label htmlFor="include-children-import" className="cursor-pointer">Include child nodes</Label>
      </div>
    </div>
  );

  const footer = (
    <PaneFooter onCancel={onClose} onSubmit={handleImport} submitLabel={loading ? 'Importing...' : 'Import Node'} submitting={loading} submitDisabled={!selectedCourse || !selectedNode} submitIcon={Copy} />
  );

  return { body, footer };
}

export default function CourseStructureBuilder() {
  const { courseId } = useParams();
  const { api, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [course, setCourse] = useState(null);
  useTabLabel(course?.title ? `Structure: ${course.title}` : null);
  const [flatNodes, setFlatNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState(null);
  // Explorer column IS the structure tree: clicking a chapter/topic/subtopic of this course selects it here,
  // and the 3-dot menu carries the node actions (add child, edit, copy, move, indent/outdent, delete).
  const handlersRef = useRef({});
  const explorer = useCourseExplorer({
    api,
    selectedKey: selectedNode ? `node:${selectedNode.id}` : `course:${courseId}`,
    storageKey: 'courses',
    onNode: (node, entry) => {
      if (entry?.course?.data.id !== courseId) return false;
      const hit = handlersRef.current.flatNodes?.find((n) => n.id === node.data.id);
      if (!hit) return false;
      handlersRef.current.select?.(hit);
      return true;
    },
    nodeMenu: (node, entry) => {
      if (entry?.course?.data.id !== courseId) return null;
      const hit = handlersRef.current.flatNodes?.find((n) => n.id === node.data.id);
      return hit ? handlersRef.current.menuFor?.(hit) : null;
    },
    courseMenu: (node) => (node.data.id === courseId ? [
      { key: 'add', label: 'Add chapter', icon: Plus, onClick: () => handlersRef.current.addRoot?.() },
      { key: 'import', label: 'Import from course…', icon: Copy, onClick: () => handlersRef.current.openImport?.() },
      { separator: true },
      { key: 'edit', label: 'Edit course', icon: Pencil, onClick: () => navigate(`/instructor/courses/${courseId}/edit`) },
      { key: 'preview', label: 'Preview as learner', icon: Eye, onClick: () => openLearnerPreview(`/course/${courseId}`) },
    ] : null),
  });
  
  // Side pane states (copy / import open in the resizable pane instead of popups)
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copySourceNode, setCopySourceNode] = useState(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  
  const fetchCourseTree = useCallback(async () => {
    try {
      const res = await api.get(`/courses/${courseId}/tree`);
      setCourse(res.data.course);
      
      const flat = flattenTree(res.data.tree);
      setFlatNodes(flat);
      // ?node=<id> (from the explorer) selects that chapter / topic / subtopic
      const wanted = searchParams.get('node');
      if (wanted) {
        const hit = flat.find((n) => n.id === wanted);
        if (hit) setSelectedNode(hit);
      }
    } catch (error) {
      toast.error('Failed to load course structure');
      navigate('/instructor/courses');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, courseId, navigate, searchParams.get('node')]);
  
  useEffect(() => {
    fetchCourseTree();
  }, [fetchCourseTree]);
  // keep the explorer in sync with edits made here (add / rename / move / delete)
  const explorerReload = explorer.reload;
  useEffect(() => { if (!loading) explorerReload(); }, [flatNodes, loading, explorerReload]);

  const closeCopyPane = () => { setCopyModalOpen(false); setCopySourceNode(null); };
  const closeImportPane = () => setImportModalOpen(false);
  const copyPane = useCopyNodePane({
    isOpen: copyModalOpen,
    onClose: closeCopyPane,
    sourceNode: copySourceNode,
    currentCourseId: courseId,
    api,
    onSuccess: fetchCourseTree,
  });
  const importPane = useImportNodePane({
    isOpen: importModalOpen,
    onClose: closeImportPane,
    currentCourseId: courseId,
    targetParentId: selectedNode?.node_type === 'section' || selectedNode?.node_type === 'subsection' ? selectedNode?.id : null,
    api,
    onSuccess: fetchCourseTree,
  });

  // Resizable pane handlers
  
  
  const handleSelectNode = (node) => {
    setSelectedNode(node);
  };
  
  const handleAddRoot = () => {
    navigate(`/instructor/courses/${courseId}/nodes/new`);
  };
  
  const handleAddChild = (parentNode) => {
    navigate(`/instructor/courses/${courseId}/nodes/new?parentId=${parentNode.id}`);
  };
  
  const handleEditNode = (node) => {
    navigate(`/instructor/courses/${courseId}/nodes/${node.id}`);
  };
  
  const handleDeleteNode = async (node) => {
    if (!window.confirm(`Delete "${node.title}" and all its children?`)) return;
    try {
      await api.delete(`/courses/${courseId}/nodes/${node.id}`);
      toast.success('Node deleted');
      if (selectedNode?.id === node.id) setSelectedNode(null);
      fetchCourseTree();
    } catch (error) {
      toast.error('Failed to delete node');
    }
  };

  const handleCopyNode = (node) => {
    setImportModalOpen(false); // one pane at a time
    setCopySourceNode(node);
    setCopyModalOpen(true);
  };

  // Get siblings of a node (nodes with same parent)
  const getSiblings = (node) => {
    return flatNodes.filter(n => n.parent_id === node.parent_id).sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
  };

  // Move node up within siblings
  const handleMoveUp = async (node) => {
    const siblings = getSiblings(node);
    const currentIndex = siblings.findIndex(s => s.id === node.id);
    if (currentIndex <= 0) return;

    const prevSibling = siblings[currentIndex - 1];
    
    try {
      await api.post(`/courses/${courseId}/nodes/reorder`, {
        reorders: [
          { node_id: node.id, new_parent_id: node.parent_id, new_order_index: prevSibling.order_index || 0 },
          { node_id: prevSibling.id, new_parent_id: prevSibling.parent_id, new_order_index: node.order_index || 0 }
        ]
      });
      fetchCourseTree();
      toast.success('Node moved up');
    } catch (error) {
      toast.error('Failed to move node');
    }
  };

  // Move node down within siblings
  const handleMoveDown = async (node) => {
    const siblings = getSiblings(node);
    const currentIndex = siblings.findIndex(s => s.id === node.id);
    if (currentIndex >= siblings.length - 1) return;

    const nextSibling = siblings[currentIndex + 1];
    
    try {
      await api.post(`/courses/${courseId}/nodes/reorder`, {
        reorders: [
          { node_id: node.id, new_parent_id: node.parent_id, new_order_index: nextSibling.order_index || 0 },
          { node_id: nextSibling.id, new_parent_id: nextSibling.parent_id, new_order_index: node.order_index || 0 }
        ]
      });
      fetchCourseTree();
      toast.success('Node moved down');
    } catch (error) {
      toast.error('Failed to move node');
    }
  };

  // Indent: Make node a child of the sibling above it
  const handleIndent = async (node) => {
    const siblings = getSiblings(node);
    const currentIndex = siblings.findIndex(s => s.id === node.id);
    if (currentIndex <= 0) return; // Can't indent if no sibling above
    
    const siblingAbove = siblings[currentIndex - 1];
    
    try {
      // Get children of the sibling above to find the right order_index
      const childrenOfTarget = flatNodes.filter(n => n.parent_id === siblingAbove.id);
      const newOrderIndex = childrenOfTarget.length > 0 
        ? Math.max(...childrenOfTarget.map(c => c.order_index || 0)) + 1 
        : 0;
      
      await api.post(`/courses/${courseId}/nodes/reorder`, {
        reorders: [
          { node_id: node.id, new_parent_id: siblingAbove.id, new_order_index: newOrderIndex }
        ]
      });
      
      // Expand the new parent so user can see the moved node
      
      fetchCourseTree();
      toast.success('Node moved to subtopic');
    } catch (error) {
      toast.error('Failed to indent node');
    }
  };

  // Outdent: Move node to parent's level (become sibling of parent)
  const handleOutdent = async (node) => {
    if (!node.parent_id) return; // Already at root level
    
    const parent = flatNodes.find(n => n.id === node.parent_id);
    if (!parent) return;
    
    try {
      // Get siblings of the parent to find the right order_index
      const parentSiblings = flatNodes.filter(n => n.parent_id === parent.parent_id);
      const parentIndex = parentSiblings.findIndex(s => s.id === parent.id);
      const newOrderIndex = parentIndex >= 0 ? (parent.order_index || 0) + 1 : 0;
      
      await api.post(`/courses/${courseId}/nodes/reorder`, {
        reorders: [
          { node_id: node.id, new_parent_id: parent.parent_id, new_order_index: newOrderIndex }
        ]
      });
      
      fetchCourseTree();
      toast.success('Node moved to parent level');
    } catch (error) {
      toast.error('Failed to outdent node');
    }
  };

  // Check if node can be indented (has a sibling above)
  const canIndent = (node) => {
    const siblings = getSiblings(node);
    const currentIndex = siblings.findIndex(s => s.id === node.id);
    return currentIndex > 0;
  };

  // Check if node can be outdented (has a parent)
  const canOutdent = (node) => {
    return !!node.parent_id;
  };

  // Drag and drop handler

  const isPdfFile = (fileName) => {
    return fileName?.toLowerCase().endsWith('.pdf');
  };
  
  if (loading) {
    return (
      <DashboardLayout>
        <RecordPage title="Course structure" loading={loading} onBack={() => navigate('/instructor/courses')} maxWidth="none" />
      </DashboardLayout>
    );
  }

  // Latest handlers for the explorer menu (the hook is created before these are defined)
  handlersRef.current = {
    flatNodes,
    select: handleSelectNode,
    addRoot: handleAddRoot,
    openImport: () => { setCopyModalOpen(false); setCopySourceNode(null); setImportModalOpen(true); },
    menuFor: (node) => {
      const siblings = getSiblings(node);
      const i = siblings.findIndex((x) => x.id === node.id);
      // anything can hold children: chapters hold topics, topics hold subtopics, subtopics hold more subtopics (no depth limit)
      const addLabel = node.node_type === 'section' ? 'Add topic / subtopic' : node.node_type === 'subsection' ? 'Add subtopic' : 'Add subtopic underneath';
      return [
        { key: 'add-child', label: addLabel, icon: Plus, onClick: () => handleAddChild(node) }, { separator: true },
        { key: 'edit', label: 'Edit', icon: Pencil, onClick: () => handleEditNode(node) },
        { key: 'copy', label: 'Copy to course…', icon: Copy, onClick: () => handleCopyNode(node) },
        { separator: true },
        { key: 'up', label: 'Move up', icon: ArrowUp, onClick: () => handleMoveUp(node), disabled: i <= 0 },
        { key: 'down', label: 'Move down', icon: ArrowDown, onClick: () => handleMoveDown(node), disabled: i >= siblings.length - 1 },
        { key: 'outdent', label: 'Move to parent level', icon: ArrowBendUpLeft, onClick: () => handleOutdent(node), disabled: !canOutdent(node) },
        { key: 'indent', label: 'Make child of the one above', icon: ArrowBendDownRight, onClick: () => handleIndent(node), disabled: !canIndent(node) },
        { separator: true },
        { key: 'delete', label: 'Delete', icon: Trash, onClick: () => handleDeleteNode(node), danger: true },
      ];
    },
  };

  const SelectedIcon = selectedNode ? getNodeIcon(selectedNode.node_type, selectedNode.content_type) : FolderSimple;

  // ─── Side pane ───
  let side = null;
  if (copyModalOpen) {
    side = { open: true, onOpenChange: closeCopyPane, title: 'Copy node to another course', subtitle: copySourceNode?.title, icon: Copy, children: copyPane.body, footer: copyPane.footer, testid: 'copy-node-pane' };
  } else if (importModalOpen) {
    side = { open: true, onOpenChange: closeImportPane, title: 'Import from course', subtitle: selectedNode?.node_type === 'section' || selectedNode?.node_type === 'subsection' ? `Into: ${selectedNode.title}` : 'Into the root level', icon: Copy, children: importPane.body, footer: importPane.footer, testid: 'import-node-pane' };
  }

  return (
    <DashboardLayout>
      <ExplorerLayout explorer={explorer.props}>
      <RecordPage
        data-testid="course-structure-builder"
        title={course?.title}
        subtitle="Course structure"
        badge={course && <StatusBadge value={course.is_published ? 'published' : 'draft'} />}
        onBack={() => navigate('/instructor/courses')}
        onRefresh={fetchCourseTree}
        maxWidth="none"
        side={side}
        primary={[
          { key: 'add', label: 'Add node', icon: Plus, onClick: handleAddRoot, testid: 'add-root-node' },
          { key: 'import', label: 'Import from course', icon: Copy, variant: 'outline', onClick: () => { setCopyModalOpen(false); setCopySourceNode(null); setImportModalOpen(true); }, testid: 'import-node-btn' },
        ]}
        secondary={[
          { key: 'edit', label: 'Edit course', icon: Pencil, onClick: () => navigate(`/instructor/courses/${courseId}/edit`) },
          { key: 'preview', label: 'Preview', icon: Eye, onClick: () => openLearnerPreview(`/course/${courseId}`) },
        ]}
      >
        {/* Main Content: two resizable panes filling the remaining height */}
        <div className="h-[calc(100vh-14rem)] flex overflow-hidden bg-white border border-border rounded-lg shadow-sm">
          {flatNodes.length === 0 && (
            <div className="w-full flex flex-col items-center justify-center text-muted-foreground py-16">
              <FolderSimple className="w-12 h-12 mb-3 opacity-20" />
              <p className="text-sm mb-3">No chapters yet — build the structure from the tree on the left or add the first chapter.</p>
              <Button size="sm" onClick={handleAddRoot} data-testid="add-first-node"><Plus className="w-4 h-4 mr-1" />Add first chapter</Button>
            </div>
          )}
          
          {/* Content Panel */}
          <div className="flex-1 overflow-hidden">
            {!selectedNode ? (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <FolderSimple className="w-16 h-16 mx-auto mb-4 opacity-20" />
                  <p className="mb-2">Select a node to view details</p>
                  <p className="text-sm">Drag nodes to reorder, or use the menu for Move Up/Down</p>
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col">
                <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <SelectedIcon className="w-5 h-5 text-primary" weight="duotone" />
                    </div>
                    <div>
                      <h2 className="font-heading font-semibold">{selectedNode.title}</h2>
                      <p className="text-sm text-muted-foreground capitalize">
                        {NODE_KIND_LABEL[selectedNode.node_type] || selectedNode.node_type} {selectedNode.content_type && `- ${selectedNode.content_type}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => handleCopyNode(selectedNode)} className="gap-2">
                      <Copy className="w-4 h-4" />Copy
                    </Button>
                    <Button onClick={() => handleEditNode(selectedNode)} className="gap-2" data-testid="edit-selected-node">
                      <Pencil className="w-4 h-4" />Edit Node
                    </Button>
                  </div>
                </div>
                
                <div className="flex-1 overflow-auto p-6">
                  {/* Tutor overview (learners see it in the lesson's Overview tab) */}
                  {selectedNode.overview && (
                    <div className="mb-6 rounded border border-sky-200 bg-sky-50/60 px-4 py-3" data-testid="node-overview">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-sky-800 mb-1">Lesson overview · shown to learners</div>
                      <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: selectedNode.overview }} />
                    </div>
                  )}
                  {/* Rich text content */}
                  {selectedNode.content_text && (
                    <div className="prose prose-sm max-w-none dark:prose-invert mb-6" dangerouslySetInnerHTML={{ __html: selectedNode.content_text }} />
                  )}
                  
                  {/* Video */}
                  {selectedNode.content_type === 'video' && selectedNode.external_url && (() => {
                    const url = selectedNode.external_url;
                    const isApiVideo = url.startsWith('/api/files/');
                    const embedUrl = getEmbedUrl(url);
                    const isEmbeddable = embedUrl && (
                      embedUrl.includes('youtube.com/embed') || 
                      embedUrl.includes('player.vimeo.com') || 
                      embedUrl.includes('cloudflarestream.com')
                    );
                    
                    return (
                      <div className="aspect-video bg-black rounded-lg overflow-hidden mb-6">
                        {isEmbeddable ? (
                          <iframe 
                            src={embedUrl} 
                            className="w-full h-full" 
                            allowFullScreen 
                            title={selectedNode.title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          />
                        ) : (
                          <video
                            src={isApiVideo 
                              ? `${process.env.REACT_APP_BACKEND_URL}${url}/stream?token=${encodeURIComponent(token)}`
                              : url
                            }
                            className="w-full h-full"
                            controls
                            controlsList="nodownload"
                            title={selectedNode.title}
                            data-testid="structure-video-player"
                          />
                        )}
                      </div>
                    );
                  })()}
                  
                  {/* External link */}
                  {selectedNode.content_type === 'external_link' && selectedNode.external_url && (
                    <div className="p-4 bg-muted rounded-lg mb-6">
                      <p className="text-sm text-muted-foreground mb-2">External Resource:</p>
                      <a href={selectedNode.external_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-2">
                        <LinkIcon className="w-4 h-4" />{selectedNode.external_url}
                      </a>
                    </div>
                  )}
                  
                  {/* Attached Files with PDF Preview */}
                  {selectedNode.file_url && (
                    <div className="space-y-4 mb-6">
                      <p className="text-sm font-medium">Attached Files:</p>
                      {selectedNode.file_url.split(',').filter(f => f.trim()).map((fileData, idx) => {
                        const parts = fileData.split('|');
                        const filePath = parts[0];
                        const fileName = parts[1] || filePath.split('/').pop();
                        const isPdf = isPdfFile(fileName);
                        
                        return (
                          <div key={idx} className="border rounded-lg overflow-hidden">
                            <div className="p-3 bg-muted/50 flex items-center gap-2">
                              {isPdf ? <FilePdf className="w-4 h-4 text-red-500" /> : <File className="w-4 h-4 text-primary" />}
                              <span className="text-sm font-medium">{fileName}</span>
                            </div>
                            {isPdf && (
                              <div className="p-3">
                                <PdfViewer filePath={filePath} token={token} />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Exam info */}
                  {selectedNode.node_type === 'exam' && (
                    <div className="p-6 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800 mb-6">
                      <Exam className="w-12 h-12 text-amber-600 mb-4" weight="duotone" />
                      <h3 className="font-heading font-semibold text-lg mb-2">Exam Node</h3>
                      <p className="text-muted-foreground">This node represents an exam assessment.</p>
                    </div>
                  )}
                  
                  {/* Section/Subsection info */}
                  {(selectedNode.node_type === 'section' || selectedNode.node_type === 'subsection') && !selectedNode.content_text && !selectedNode.file_url && (
                    <div className="p-6 bg-muted/50 rounded-lg text-center">
                      <FolderSimple className="w-12 h-12 mx-auto text-muted-foreground mb-4" weight="duotone" />
                      <h3 className="font-heading font-semibold text-lg mb-2">
                        {selectedNode.node_type === 'section' ? 'Chapter' : 'Topic'}
                      </h3>
                      <p className="text-muted-foreground">{selectedNode.node_type === 'section' ? 'A chapter groups topics and subtopics.' : 'A topic groups subtopics (lessons, files, exams).'}</p>
                      <p className="text-sm text-muted-foreground mt-2">Use the menu to add child nodes or reorder.</p>
                    </div>
                  )}
                  
                  {/* Empty content node */}
                  {selectedNode.node_type === 'content' && !selectedNode.content_text && !selectedNode.file_url && !selectedNode.external_url && (
                    <div className="p-6 bg-muted/50 rounded-lg text-center">
                      <File className="w-12 h-12 mx-auto text-muted-foreground mb-4" weight="duotone" />
                      <h3 className="font-heading font-semibold text-lg mb-2">No Content Yet</h3>
                      <p className="text-muted-foreground mb-4">This node doesn't have any content.</p>
                      <Button onClick={() => handleEditNode(selectedNode)} className="gap-2">
                        <Pencil className="w-4 h-4" />Add Content
                      </Button>
                    </div>
                  )}

                  {/* Node metadata */}
                  <div className="mt-6 p-4 bg-muted/30 rounded-lg">
                    <h4 className="font-medium text-sm mb-3">Node Details</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-muted-foreground">Type:</span>
                        <span className="ml-2">{NODE_KIND_LABEL[selectedNode.node_type] || selectedNode.node_type}</span>
                      </div>
                      {selectedNode.content_type && (
                        <div>
                          <span className="text-muted-foreground">Content:</span>
                          <span className="ml-2 capitalize">{selectedNode.content_type}</span>
                        </div>
                      )}
                      <div>
                        <span className="text-muted-foreground">Required:</span>
                        <span className="ml-2">{selectedNode.is_required ? 'Yes' : 'No'}</span>
                      </div>
                      {selectedNode.duration_minutes > 0 && (
                        <div>
                          <span className="text-muted-foreground">Duration:</span>
                          <span className="ml-2">{selectedNode.duration_minutes} min</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

      </RecordPage>
      </ExplorerLayout>
    </DashboardLayout>
  );
}
